import { products } from './products.js';

// Landing-only portrait revisions; canonical catalog and variant imagery stay intact.
const landingPortraitPairs = Object.freeze({
  hoodie: Object.freeze({
    front: '/landing/winter/portraits/hoodie-front.webp',
    back: '/landing/winter/portraits/hoodie-back.webp',
  }),
  longsleeve_dryfit: Object.freeze({
    front: '/landing/winter/portraits/longsleeve-dryfit-front.webp',
    back: '/landing/winter/portraits/longsleeve-dryfit-back.webp',
  }),
});

function catalogRaster(value, sku, side) {
  const candidates = Array.isArray(value) ? value : [value];
  const source = candidates.find((candidate) => typeof candidate === 'string' && /\.(jpe?g|png)$/i.test(candidate))
    || candidates.find((candidate) => typeof candidate === 'string' && /\.(avif|webp)$/i.test(candidate));

  if (!source) throw new Error(`Missing catalog ${side} image for winter SKU "${sku}"`);

  // Match Catalog's base1/base2 wearing portraits, not the colour/order resolver.
  const rewritten = source.replace(/^(.*)\/base_(1|2)(\.[^.]+)?$/, (_match, _prefix, number, extension) => (
    `/product_images/${sku}/base${number}_${sku}${extension || '.png'}`
  ));
  return { src: rewritten, webp: rewritten.replace(/\.[^.]+$/, '.webp') };
}

export function getWinterCatalogImages(product) {
  if (!product || typeof product.sku !== 'string' || !product.sku.trim()) {
    throw new Error('Winter catalog images require an explicit product SKU');
  }
  return {
    front: catalogRaster(product.images?.base1, product.sku, 'front'),
    back: catalogRaster(product.images?.base2, product.sku, 'back'),
  };
}

export function getWinterLandingImages(product) {
  const canonicalImages = getWinterCatalogImages(product);
  if (!Object.prototype.hasOwnProperty.call(landingPortraitPairs, product.sku)) return canonicalImages;

  const pair = landingPortraitPairs[product.sku];
  return {
    front: { src: pair.front, webp: pair.front },
    back: { src: pair.back, webp: pair.back },
  };
}

export function resolveWinterProducts(catalog = products) {
  if (!Array.isArray(catalog)) throw new Error('Winter products require a catalog array');
  const seen = new Set();
  return catalog
    .filter((product) => Array.isArray(product.search_tag) && product.search_tag.includes('winter'))
    .sort((a, b) => (a.appearance || 0) - (b.appearance || 0))
    .map((product) => {
      if (seen.has(product.sku)) throw new Error(`Duplicate winter SKU "${product.sku}"`);
      if (typeof product.nameHe !== 'string' || !product.nameHe.trim()) {
        throw new Error(`Missing Hebrew catalog name for winter SKU "${product.sku}"`);
      }
      seen.add(product.sku);
      return { product, images: getWinterLandingImages(product) };
    });
}
